import { defineStore } from "pinia";
import { type AccessOrigin, probeAccess } from "@/api/access";
import { ApiError } from "@/api/request";
import { useAnnouncer } from "@/composables/useAnnouncer";

const ORDER: AccessOrigin[] = ["defender", "watcher"];

// REQ-CD-P1-CON-10 / D-CD-60 — per-origin sign-in state, in memory only
// (SEC-CD-P1-24: no navigation to survive, so no storage is needed).
export const useAccessStore = defineStore("access", {
  state: () => ({
    required: { defender: null, watcher: null } as Record<AccessOrigin, number | null>,
    checking: false,
  }),
  getters: {
    requiredOrigins: (state): AccessOrigin[] => ORDER.filter((o) => state.required[o] !== null),
  },
  actions: {
    markRequired(origin: AccessOrigin, status: number): void {
      const { announce } = useAnnouncer();
      const wasOk = this.required[origin] === null;
      this.required[origin] = status;
      if (wasOk) {
        const label = origin === "defender" ? "Defender" : "Watcher";
        announce(`Sign-in to ${label} required`);
      }
    },

    markOk(origin: AccessOrigin): void {
      const { announce } = useAnnouncer();
      const wasRequired = this.required[origin] !== null;
      this.required[origin] = null;
      if (wasRequired) {
        const label = origin === "defender" ? "Defender" : "Watcher";
        announce(`Signed in to ${label}`);
      }
    },

    async recheck(): Promise<void> {
      if (this.checking || this.requiredOrigins.length === 0) return;
      this.checking = true;
      try {
        for (const origin of this.requiredOrigins) {
          try {
            await probeAccess(origin);
            this.markOk(origin);
          } catch (err) {
            if (err instanceof ApiError && err.accessRequired) {
              this.markRequired(origin, err.status);
            }
            // any other rejection leaves the flag as it is
          }
        }
      } finally {
        this.checking = false;
      }
    },
  },
});
